import { PayableRow } from "@wingspanhq/wingspan-api-sdk";
import { WSQueryCache } from "@ws-react-query";
import { payablesApi } from "@api";
import { queriesPayableRows, queriesPayables } from "@query";
import {
  BulkActionOnProgress,
  BulkActionProgress,
  bulkActionFactory,
  useForegroundBulkAction
} from "../../BulkActions";
import { QUERY_PAYABLE } from "../../../query/payments/keys";

const invalidateAfterMutation = (successItems: PayableRow[]) => {
  WSQueryCache.invalidateQueries(
    queriesPayableRows.searchPayableRows.queryKeyBase
  );
  WSQueryCache.invalidateQueries(queriesPayables.getPayable.queryKeyBase);
  successItems.forEach(payable => {
    if (payable.id) {
      WSQueryCache.invalidateQueries([QUERY_PAYABLE, payable.id]);
    }
  });
};

const processOpenPayables = (
  items: PayableRow[],
  onProgress: BulkActionOnProgress,
  onSuccess?: () => void
): Promise<BulkActionProgress> => {
  const bulkAction = bulkActionFactory<PayableRow>({
    onProgress,
    action: async item => {
      if (!item.id) {
        throw new Error("Payable is missing id");
      }
      return payablesApi.openPayable({ payableId: item.id, ifMatch: "*" });
    },
    onFinish: ({ successItems }) => {
      invalidateAfterMutation(successItems);
      onSuccess?.();
    }
  });

  return bulkAction.run(items).then(({ successItems, failedItems }) => ({
    total: items.length,
    processed: successItems.length + failedItems.length,
    successful: successItems.length,
    failed: failedItems.length
  }));
};

export const useBulkOpenPayables = () => {
  const runBulkAction = useForegroundBulkAction();

  return (items: PayableRow[], onSuccess?: () => void) =>
    runBulkAction({
      actionName: "Open payables",
      itemCount: items.length,
      confirmDescription: "Open selected draft payables",
      confirmLabel: "Open",
      prepare: async () => items,
      process: async (data, onProgress) =>
        processOpenPayables(data, onProgress, onSuccess)
    });
};
